import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api';

interface AppDeviceUser {
  _id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface AppDeviceRow {
  _id: string;
  deviceId: string;
  platform?: string;
  enabled: boolean;
  user: AppDeviceUser | null;
  userLinkedAt?: string | Date | null;
  lastCheckInAt?: string | Date | null;
  createdAt?: string | Date | null;
}

const formatWhen = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const AppDevicesTable: React.FC = () => {
  const [rows, setRows] = useState<AppDeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState('');
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AppDeviceRow | null>(null);
  const [deleteUserToo, setDeleteUserToo] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/app-devices`, { withCredentials: true });
      setRows(response.data.appDevices || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching app devices:', err);
      setError(err.response?.data?.message || 'Failed to load app devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextId = deviceId.trim();
    if (!nextId) return;
    setAdding(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/app-devices`,
        { deviceId: nextId },
        { withCredentials: true }
      );
      setRows((prev) => [response.data.appDevice, ...prev]);
      setDeviceId('');
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add device ID');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleEnabled = async (row: AppDeviceRow, enabled: boolean) => {
    setRows((prev) => prev.map((entry) => (entry._id === row._id ? { ...entry, enabled } : entry)));
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/app-devices/${row._id}`,
        { enabled },
        { withCredentials: true }
      );
      setRows((prev) => prev.map((entry) => (entry._id === row._id ? response.data.appDevice : entry)));
    } catch (err: any) {
      setRows((prev) => prev.map((entry) => (entry._id === row._id ? row : entry)));
      setError(err.response?.data?.message || 'Failed to update device');
    }
  };

  const openDelete = (row: AppDeviceRow) => {
    setPendingDelete(row);
    setDeleteUserToo(false);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/app-devices/${pendingDelete._id}`,
        { withCredentials: true, params: { deleteUser: deleteUserToo ? '1' : undefined } }
      );
      setRows((prev) => prev.filter((entry) => entry._id !== pendingDelete._id));
      setPendingDelete(null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete device');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-5">
      <div className="mb-3">
        <h4 className="mb-1">Enable User Login (By Device ID)</h4>
        <div className="text-muted small">
          Phones in this table see account login in the app. When that phone creates
          an account, the user column fills in. Disable keeps the row but hides login.
        </div>
      </div>

      <form onSubmit={handleAdd} className="d-flex flex-wrap gap-2 mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '360px' }}
          placeholder="Add device ID"
          value={deviceId}
          onChange={(event) => setDeviceId(event.target.value)}
        />
        <button type="submit" className="btn btn-outline-primary" disabled={adding || !deviceId.trim()}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          {adding ? 'Adding…' : 'Add device'}
        </button>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Device ID</th>
              <th>User account</th>
              <th>Linked</th>
              <th>Last check-in</th>
              <th>Enabled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <tr key={row._id} className={row.enabled ? undefined : 'text-muted'}>
                  <td className="text-break">{row.deviceId}</td>
                  <td>
                    {row.user?._id ? (
                      <Link to={`/users/${row.user._id}`}>
                        {row.user.email || `${row.user.firstName || ''} ${row.user.lastName || ''}`.trim() || row.user._id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatWhen(row.userLinkedAt)}</td>
                  <td>{formatWhen(row.lastCheckInAt)}</td>
                  <td>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={row.enabled}
                        onChange={(event) => handleToggleEnabled(row, event.target.checked)}
                        aria-label={`${row.enabled ? 'Enabled' : 'Disabled'} for ${row.deviceId}`}
                      />
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => openDelete(row)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-1" /> Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center text-muted">No app devices yet</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {pendingDelete && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete app device</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setPendingDelete(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Remove <code>{pendingDelete.deviceId}</code> from this table?
                  </p>
                  {pendingDelete.user?._id ? (
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="delete-linked-user"
                        checked={deleteUserToo}
                        onChange={(event) => setDeleteUserToo(event.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="delete-linked-user">
                        Also delete the linked user
                        {pendingDelete.user.email ? ` (${pendingDelete.user.email})` : ''} from the users table
                      </label>
                    </div>
                  ) : (
                    <div className="text-muted small">No user account is linked to this device yet.</div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setPendingDelete(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AppDevicesTable;
